'use client';
import React, { useState, useEffect } from 'react';
import { X, Download, ExternalLink, User, Building2, Calendar, Hash, Globe, FileText, Shield, Tag, Users, BookOpen } from 'lucide-react';
import type { ThesisRecord } from './ThesisBrowser';

interface Props {
  thesis: ThesisRecord;
  onClose: () => void;
}

export default function ThesisDetailModal({ thesis, onClose }: Props) {
  const [tab, setTab] = useState<'en' | 'el'>('en');

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const hasEn = thesis.language === 'en' || thesis.language === 'both';
  const hasEl = thesis.language === 'el' || thesis.language === 'both';

  const title = tab === 'en' ? thesis.titleEn : thesis.titleEl;
  const author = tab === 'en' ? thesis.authorEn : thesis.authorEl;
  const supervisor = tab === 'en' ? thesis.supervisorEn : thesis.supervisorEl;
  const abstract = tab === 'en' ? thesis.abstractEn : thesis.abstractEl;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="thesis-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0f1a35]/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-scale-in">
        {/* Modal header */}
        <div className="bg-[#1a2e5a] px-6 py-5 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap gap-2 mb-2">
                <span className={thesis.type === 'phd' ?'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-200/30 text-purple-200 border border-purple-300/30' :'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-200/30 text-blue-200 border border-blue-300/30'
                }>
                  {thesis.type === 'phd' ? 'PhD Dissertation' : "Master's Thesis"}
                </span>
                {thesis.accessRights === 'open' && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-200/30 text-emerald-200 border border-emerald-300/30">
                    Open Access
                  </span>
                )}
              </div>
              <h2 id="thesis-modal-title" className="text-white font-700 text-lg leading-snug line-clamp-2">
                {title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 text-white/60 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-all"
              aria-label="Close modal"
            >
              <X size={18} />
            </button>
          </div>

          {/* Language tabs */}
          {(hasEn && hasEl) && (
            <div className="flex gap-1 mt-4">
              <button
                onClick={() => setTab('en')}
                className={`px-4 py-1.5 rounded-lg text-xs font-600 transition-all ${tab === 'en' ? 'bg-[#c9a84c] text-[#111e3d]' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
              >
                English
              </button>
              <button
                onClick={() => setTab('el')}
                className={`px-4 py-1.5 rounded-lg text-xs font-600 transition-all ${tab === 'el' ? 'bg-[#c9a84c] text-[#111e3d]' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
              >
                Ελληνικά
              </button>
            </div>
          )}
        </div>

        {/* Modal body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main content - left 2/3 */}
            <div className="lg:col-span-2 space-y-5">
              {/* Author & supervisor */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-600 text-[#8892a8] uppercase tracking-wide mb-1 block">Author</label>
                  <div className="flex items-center gap-2">
                    <User size={14} className="text-[#1a2e5a] flex-shrink-0" />
                    <span className="text-sm font-600 text-[#0f1a35]">{author}</span>
                  </div>
                  {thesis.orcid && (
                    <a
                      href={`https://orcid.org/${thesis.orcid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-[#c9a84c] hover:text-[#a8892e] mt-1 font-mono-data"
                    >
                      <ExternalLink size={10} />
                      ORCID: {thesis.orcid}
                    </a>
                  )}
                </div>
                <div>
                  <label className="text-xs font-600 text-[#8892a8] uppercase tracking-wide mb-1 block">Supervisor</label>
                  <div className="flex items-center gap-2">
                    <User size={14} className="text-[#1a2e5a] flex-shrink-0" />
                    <span className="text-sm font-600 text-[#0f1a35]">{supervisor}</span>
                  </div>
                </div>
              </div>

              {/* Abstract */}
              <div>
                <label className="text-xs font-600 text-[#8892a8] uppercase tracking-wide mb-2 block">Abstract</label>
                <div className="bg-[#f7f8fc] border border-[#e2e6f0] rounded-xl p-4">
                  <p className="text-sm text-[#0f1a35] leading-relaxed">{abstract}</p>
                </div>
              </div>

              {/* Keywords */}
              <div>
                <label className="text-xs font-600 text-[#8892a8] uppercase tracking-wide mb-2 block flex items-center gap-1.5">
                  <Tag size={11} />
                  Keywords (Elsevier Subject Classifications)
                </label>
                <div className="flex flex-wrap gap-2">
                  {thesis.keywords.map((kw) => (
                    <span key={`modal-kw-${thesis.id}-${kw}`} className="thesis-keyword-tag text-xs">
                      {kw}
                    </span>
                  ))}
                </div>
              </div>

              {/* Examination Committee */}
              {thesis.committee.length > 0 && (
                <div>
                  <label className="text-xs font-600 text-[#8892a8] uppercase tracking-wide mb-2 block flex items-center gap-1.5">
                    <Users size={11} />
                    Examination Committee
                  </label>
                  <div className="space-y-1.5">
                    {thesis.committee.map((member) => (
                      <div key={`committee-${thesis.id}-${member}`} className="flex items-center gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] flex-shrink-0" />
                        <span className="text-[#0f1a35] font-500">{member}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar metadata - right 1/3 */}
            <div className="space-y-4">
              {/* Download */}
              <div className="bg-[#1a2e5a]/5 border border-[#1a2e5a]/15 rounded-xl p-4">
                <h4 className="text-xs font-700 text-[#1a2e5a] uppercase tracking-wide mb-3">Download</h4>
                <div className="space-y-2">
                  {thesis.hasPdfEn && (
                    <button className="w-full flex items-center gap-2 bg-[#1a2e5a] hover:bg-[#111e3d] text-white px-3 py-2.5 rounded-lg text-xs font-600 transition-all active:scale-95">
                      <Download size={13} />
                      Download PDF (English)
                    </button>
                  )}
                  {thesis.hasPdfEl && (
                    <button className="w-full flex items-center gap-2 bg-[#c9a84c] hover:bg-[#a8892e] text-[#111e3d] px-3 py-2.5 rounded-lg text-xs font-600 transition-all active:scale-95">
                      <Download size={13} />
                      Download PDF (Greek)
                    </button>
                  )}
                  {thesis.accessRights !== 'open' && (
                    <p className="text-xs text-[#8892a8] text-center mt-1">Access restricted. Contact library.</p>
                  )}
                </div>
              </div>

              {/* Metadata details */}
              <div className="bg-white border border-[#e2e6f0] rounded-xl p-4 space-y-3">
                <MetaRow icon={<Building2 size={12} className="text-[#1a2e5a]" />} label="Department" value={thesis.department} />
                <MetaRow icon={<BookOpen size={12} className="text-[#1a2e5a]" />} label="School" value={thesis.school} />
                <MetaRow icon={<Globe size={12} className="text-[#1a2e5a]" />} label="Institution" value={thesis.institution} />
                <MetaRow icon={<Calendar size={12} className="text-[#1a2e5a]" />} label="Date of Submission" value={thesis.dateSubmission} mono />
                <MetaRow icon={<Calendar size={12} className="text-[#1a2e5a]" />} label="Date of Publication" value={thesis.datePublication} mono />
                <MetaRow icon={<Globe size={12} className="text-[#1a2e5a]" />} label="Language" value={
                  thesis.language === 'both' ? 'Greek & English' :
                  thesis.language === 'en' ? 'English' : 'Greek'
                } />
                <MetaRow icon={<Shield size={12} className="text-[#1a2e5a]" />} label="Access Rights" value={
                  thesis.accessRights === 'open' ? 'Open Access' :
                  thesis.accessRights === 'restricted' ? 'Restricted Access' : 'Embargoed'
                } />
                <MetaRow icon={<FileText size={12} className="text-[#1a2e5a]" />} label="License" value={thesis.license} />
                <div>
                  <label className="text-xs font-600 text-[#8892a8] uppercase tracking-wide mb-1 block">PID / DOI</label>
                  <a
                    href={`https://doi.org/${thesis.pid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-[#c9a84c] hover:text-[#a8892e] font-mono-data break-all"
                  >
                    <Hash size={10} className="flex-shrink-0" />
                    {thesis.pid}
                    <ExternalLink size={10} className="flex-shrink-0" />
                  </a>
                </div>
              </div>

              {/* Citation */}
              <div className="bg-white border border-[#e2e6f0] rounded-xl p-4">
                <h4 className="text-xs font-700 text-[#8892a8] uppercase tracking-wide mb-2">Cite this work</h4>
                <div className="flex flex-wrap gap-1">
                  {['APA', 'BibTeX', 'MLA', 'Chicago'].map((fmt) => (
                    <button
                      key={`cite-${thesis.id}-${fmt}`}
                      className="text-xs border border-[#e2e6f0] hover:border-[#1a2e5a] text-[#4a5578] hover:text-[#1a2e5a] px-2.5 py-1 rounded-md font-500 transition-all"
                    >
                      {fmt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaRow({ icon, label, value, mono }: { icon: React.ReactNode; label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <label className="text-xs font-600 text-[#8892a8] uppercase tracking-wide mb-0.5 block">{label}</label>
      <div className="flex items-center gap-1.5">
        {icon}
        <span className={`text-xs text-[#0f1a35] font-500 ${mono ? 'font-mono-data' : ''}`}>{value}</span>
      </div>
    </div>
  );
}